import type React from 'react';
import { useTranslation } from 'react-i18next';
import { formatGel } from '@/utils/money';
import { MetricRow, OverviewCard } from '../overviewCard';
import { formatRatio, formatSignedRatio } from '../overviewMeta';
import { CASH_COVERAGE as META } from './cashCoverageMeta';
import type { CashCoverageData } from './useCashCoverage';

type CashCoverageViewProps = {
    data: CashCoverageData;
};

/**
 * Cash coverage — render only.
 *
 * The verdict band is `accent`, not the card's own tone. A coverage ratio is a
 * reading, not a judgement: 5.41 means nothing until you know where the line
 * is, and colouring the number green would answer a question the number itself
 * is not asking. The tone lives on the icon and the delta, where it belongs.
 */
export const CashCoverageView: React.FC<CashCoverageViewProps> = ({ data }) => {
    const { t } = useTranslation('pages');

    return (
        <OverviewCard
            delta={
                data.deltaRatio === null
                    ? null
                    : {
                          label: formatSignedRatio(data.deltaRatio),
                          tone: data.deltaRatio < 0 ? 'danger' : 'success',
                      }
            }
            icon={META.icon}
            question={t(META.keys.question)}
            slot={META.slot}
            title={t(META.keys.title)}
            tone={data.tone}
            verdict={{
                label: t(META.keys.ratio),
                tone: 'accent',
                value: formatRatio(data.ratio),
            }}
        >
            <MetricRow
                label={t(META.keys.cash)}
                slot={`${META.slot}-cash`}
                tone="success"
                value={formatGel(data.cashTetri)}
            />
            <MetricRow
                label={t(META.keys.obligations)}
                slot={`${META.slot}-obligations`}
                tone="warning"
                value={formatGel(data.obligationsTetri)}
            />
        </OverviewCard>
    );
};
